import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiChevronRight, FiMail, FiPhone } from 'react-icons/fi';
import Logo from '../../images/AE-Hero.svg';

const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'collection', label: 'Information We Collect' },
    { id: 'use', label: 'How We Use Your Information' },
    { id: 'sharing', label: 'Sharing of Information' },
    { id: 'retention', label: 'Data Retention' },
    { id: 'security', label: 'Data Security' },
    { id: 'rights', label: 'Your Rights' },
    { id: 'cookies', label: 'Cookies' },
    { id: 'children', label: 'Children\'s Privacy' },
    { id: 'changes', label: 'Changes to This Policy' },
    { id: 'contact', label: 'Contact Us' },
];

const PrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (typeof window !== 'undefined') {
            const AOS = require('aos');
            AOS.refresh();
        }
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <>
            {/* ── Hero banner ── */}
            <div
                className="relative overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 60%, #2b6cb0 100%)',
                    paddingTop: 'calc(80px + 3rem)',  // navbar height + breathing room
                    paddingBottom: '5rem',
                }}
            >
                {/* Decorative rings */}
                <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full border border-white opacity-5" />
                <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full border border-white opacity-5" />

                <div className="container mx-auto px-4 relative z-10 text-center" data-aos="fade-up">
                    {/* Logo */}
                    <div className="flex justify-center mb-6">
                        <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center p-2 shadow-lg">
                            <img src={Logo} alt="Alternative Energy" className="max-h-full max-w-full object-contain" />
                        </div>
                    </div>

                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white bg-opacity-10 border border-white border-opacity-20 text-white text-sm font-medium mb-5">
                        <FiShield className="text-yellow-400" />
                        Legal Document
                    </div>

                    <h1
                        className="text-4xl md:text-5xl font-bold text-white mb-4"
                        style={{ fontFamily: 'var(--heading-font)' }}
                    >
                        Privacy Policy
                    </h1>
                    <p className="text-blue-200 max-w-2xl mx-auto text-lg mb-6">
                        Your privacy matters to us. This policy explains how Alternative Energy collects,
                        uses, and protects your personal information.
                    </p>

                    {/* Breadcrumb */}
                    <nav className="flex justify-center items-center gap-2 text-sm text-blue-300">
                        <Link to="/" className="hover:text-white transition-colors duration-200">Home</Link>
                        <FiChevronRight className="text-blue-400" />
                        <span className="text-white font-medium">Privacy Policy</span>
                    </nav>

                    <p className="mt-6 text-blue-300 text-sm">
                        Effective date: <span className="text-white font-medium">1 January 2024</span>
                        &nbsp;·&nbsp; Last updated: <span className="text-white font-medium">6 April 2026</span>
                    </p>
                </div>
            </div>

            {/* ── Body ── */}
            <div className="light-background min-h-screen">
                <div className="container mx-auto px-4 py-16">
                    <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">

                        {/* ── Sticky sidebar ── */}
                        <aside className="lg:w-72 flex-shrink-0" data-aos="fade-right">
                            <div
                                className="bg-white rounded-2xl shadow-md p-6 lg:sticky lg:top-28"
                                style={{ borderTop: '4px solid var(--accent-color)' }}
                            >
                                <p
                                    className="text-xs font-bold uppercase tracking-widest mb-4"
                                    style={{ color: 'var(--accent-color)' }}
                                >
                                    Table of Contents
                                </p>
                                <ul className="space-y-1">
                                    {sections.map((s, i) => (
                                        <li key={s.id}>
                                            <button
                                                onClick={() => scrollTo(s.id)}
                                                className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 group"
                                                style={{ color: 'var(--default-color)' }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.background = 'color-mix(in srgb, var(--accent-color), transparent 90%)';
                                                    e.currentTarget.style.color = 'var(--accent-color)';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.background = '';
                                                    e.currentTarget.style.color = 'var(--default-color)';
                                                }}
                                            >
                                                <span
                                                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white"
                                                    style={{ background: 'var(--accent-color)', opacity: 0.85 }}
                                                >
                                                    {i + 1}
                                                </span>
                                                {s.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                {/* Quick contact card */}
                                <div
                                    className="mt-6 rounded-xl p-4"
                                    style={{ background: 'color-mix(in srgb, var(--accent-color), transparent 90%)' }}
                                >
                                    <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--accent-color)' }}>
                                        Questions?
                                    </p>
                                    <a
                                        href="mailto:info@alternative.co.sz"
                                        className="flex items-center gap-2 text-sm mb-2 transition-colors duration-200"
                                        style={{ color: 'var(--heading-color)' }}
                                    >
                                        <FiMail className="flex-shrink-0" style={{ color: 'var(--accent-color)' }} />
                                        info@alternative.co.sz
                                    </a>
                                    <a
                                        href="tel:+26825185414"
                                        className="flex items-center gap-2 text-sm transition-colors duration-200"
                                        style={{ color: 'var(--heading-color)' }}
                                    >
                                        <FiPhone className="flex-shrink-0" style={{ color: 'var(--accent-color)' }} />
                                        +268 2518 5414
                                    </a>
                                </div>
                            </div>
                        </aside>

                        {/* ── Main content ── */}
                        <main className="flex-1 min-w-0" data-aos="fade-up">

                            {/* Intro card */}
                            <div
                                className="bg-white rounded-2xl shadow-sm p-8 mb-8"
                                style={{ borderLeft: '4px solid var(--accent-color)' }}
                            >
                                <p className="text-gray-600 leading-relaxed">
                                    Alternative Energy (Pty) Ltd (<strong>"Alternative Energy"</strong>, <strong>"we"</strong>,
                                    <strong>"us"</strong>, or <strong>"our"</strong>) is committed to protecting your personal
                                    information. This Privacy Policy describes how we collect, use, disclose, and safeguard
                                    information when you visit our website or use our quotation and energy services in Eswatini.
                                    Please read this policy carefully. By accessing our platform, you agree to the terms described here.
                                </p>
                            </div>

                            <div className="space-y-8">

                                {/* 1 – Overview */}
                                <PolicySection id="overview" number="1" title="Overview">
                                    <p>
                                        We operate across Eswatini providing solar, electrical, and generator services. In the course of
                                        delivering these services — including processing quotation requests, scheduling site visits, and
                                        maintaining client accounts — we collect and process personal information about our customers,
                                        prospective customers, and website visitors.
                                    </p>
                                    <p>
                                        This policy applies to all personal data processed through our website (
                                        <a href="https://alternative.co.sz" style={{ color: 'var(--accent-color)' }}>alternative.co.sz</a>
                                        ), our client portal, and any related digital channels operated by Alternative Energy.
                                    </p>
                                </PolicySection>

                                {/* 2 – Collection */}
                                <PolicySection id="collection" number="2" title="Information We Collect">
                                    <p>We collect information in the following ways:</p>

                                    <SubHeading>Information You Provide Directly</SubHeading>
                                    <BulletList items={[
                                        'Full name, email address, and phone number when creating an account or submitting a quotation request.',
                                        'Property address and site details needed to assess and deliver our services.',
                                        'Preferred contact method and appointment preferences.',
                                        'Messages and correspondence you send through our contact form or email.',
                                        'Payment and billing information when engaging paid services (processed securely through our payment partners).',
                                    ]} />

                                    <SubHeading>Information Collected Automatically</SubHeading>
                                    <BulletList items={[
                                        'IP address, browser type and version, and device information.',
                                        'Pages visited, time spent on pages, and navigation patterns.',
                                        'Referring URLs and search terms that brought you to our site.',
                                        'Cookie identifiers and session data (see the Cookies section below).',
                                    ]} />

                                    <SubHeading>Information from Third Parties</SubHeading>
                                    <BulletList items={[
                                        'Social login data (name, email, profile picture) if you choose to sign in via Google, GitHub, or Facebook.',
                                        'Publicly available information relevant to service delivery (e.g. property records for site assessment purposes).',
                                    ]} />
                                </PolicySection>

                                {/* 3 – Use */}
                                <PolicySection id="use" number="3" title="How We Use Your Information">
                                    <p>We use the personal information we collect for the following purposes:</p>
                                    <BulletList items={[
                                        'To create and manage your account on our client portal.',
                                        'To process, fulfil, and follow up on quotation requests for solar, electrical, and generator services.',
                                        'To schedule and coordinate site visits, installations, and maintenance appointments.',
                                        'To communicate with you about your account, quotations, and service updates.',
                                        'To send you relevant service information or promotional material where you have consented to receive it.',
                                        'To improve our website and service offerings based on aggregated usage patterns.',
                                        'To comply with legal and regulatory obligations applicable in Eswatini.',
                                        'To detect, prevent, and respond to fraud, security incidents, and misuse of our platform.',
                                        'To manage our internal business operations including invoicing, record-keeping, and reporting.',
                                    ]} />
                                    <p>
                                        We will only use your personal information for purposes compatible with those listed above. If we
                                        need to use it for a materially different purpose, we will notify you in advance.
                                    </p>
                                </PolicySection>

                                {/* 4 – Sharing */}
                                <PolicySection id="sharing" number="4" title="Sharing of Information">
                                    <p>
                                        We do not sell, trade, or rent your personal information to third parties. We may share your
                                        information only in the following limited circumstances:
                                    </p>
                                    <BulletList items={[
                                        'With service providers who assist us in operating our platform (e.g. hosting, email delivery, payment processing) — these providers are bound by confidentiality obligations and may only process data on our behalf.',
                                        'With our installation and technical teams where necessary to deliver a service you have requested.',
                                        'With regulatory or law enforcement authorities if required by law or to protect the rights, safety, and property of Alternative Energy or our clients.',
                                        'With a successor business entity in the event of a merger, acquisition, or sale of assets — you will be notified of any such transfer.',
                                    ]} />
                                    <InfoBox>
                                        We never share your contact details with third-party marketing organisations or data brokers.
                                    </InfoBox>
                                </PolicySection>

                                {/* 5 – Retention */}
                                <PolicySection id="retention" number="5" title="Data Retention">
                                    <p>
                                        We retain your personal information for as long as is necessary to fulfil the purposes described
                                        in this policy, including for legal, accounting, and reporting requirements.
                                    </p>
                                    <BulletList items={[
                                        'Active account data is retained for the lifetime of your account plus five (5) years after closure.',
                                        'Quotation records are retained for seven (7) years from the date of submission to comply with financial and tax regulations in Eswatini.',
                                        'Website usage logs are retained for up to twelve (12) months.',
                                        'Marketing consent records are retained until you withdraw consent plus three (3) years.',
                                    ]} />
                                    <p>
                                        When data is no longer required, it is securely deleted or anonymised in accordance with our
                                        internal data disposal procedures.
                                    </p>
                                </PolicySection>

                                {/* 6 – Security */}
                                <PolicySection id="security" number="6" title="Data Security">
                                    <p>
                                        We implement appropriate technical and organisational measures to protect your personal
                                        information against unauthorised access, alteration, disclosure, or destruction. These include:
                                    </p>
                                    <BulletList items={[
                                        'TLS/HTTPS encryption for all data transmitted between your browser and our servers.',
                                        'Hashed and salted storage of account passwords — we never store passwords in plain text.',
                                        'JWT-based authentication with automatic session expiry.',
                                        'Role-based access controls limiting staff access to data on a need-to-know basis.',
                                        'Regular security reviews of our application and infrastructure.',
                                    ]} />
                                    <p>
                                        While we take every reasonable precaution, no method of electronic transmission or storage is
                                        100% secure. If you believe your account has been compromised, please contact us immediately.
                                    </p>
                                </PolicySection>

                                {/* 7 – Rights */}
                                <PolicySection id="rights" number="7" title="Your Rights">
                                    <p>
                                        You have the following rights regarding your personal information. To exercise any of these
                                        rights, please contact us using the details in the <em>Contact Us</em> section below.
                                    </p>

                                    <div className="grid sm:grid-cols-2 gap-4 my-6">
                                        {[
                                            { title: 'Right of Access', desc: 'Request a copy of the personal information we hold about you.' },
                                            { title: 'Right to Rectification', desc: 'Ask us to correct inaccurate or incomplete information.' },
                                            { title: 'Right to Erasure', desc: 'Request deletion of your data where there is no legal basis for retention.' },
                                            { title: 'Right to Restriction', desc: 'Ask us to limit the processing of your data in certain circumstances.' },
                                            { title: 'Right to Portability', desc: 'Receive your data in a structured, machine-readable format.' },
                                            { title: 'Right to Object', desc: 'Object to processing of your data for direct marketing at any time.' },
                                        ].map(r => (
                                            <div
                                                key={r.title}
                                                className="bg-white border rounded-xl p-4"
                                                style={{ borderColor: 'color-mix(in srgb, var(--accent-color), transparent 70%)' }}
                                            >
                                                <p className="font-semibold mb-1 text-sm" style={{ color: 'var(--heading-color)' }}>{r.title}</p>
                                                <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
                                            </div>
                                        ))}
                                    </div>

                                    <p>
                                        We will respond to all requests within <strong>30 days</strong>. In complex cases we may extend
                                        this period by a further 60 days, in which case we will notify you of the delay and reason.
                                    </p>
                                </PolicySection>

                                {/* 8 – Cookies */}
                                <PolicySection id="cookies" number="8" title="Cookies">
                                    <p>
                                        Our website uses cookies and similar technologies to enhance your browsing experience and
                                        understand how our platform is used. We use the following types of cookies:
                                    </p>
                                    <div className="overflow-x-auto my-4">
                                        <table className="w-full text-sm border-collapse">
                                            <thead>
                                                <tr style={{ background: 'var(--accent-color)' }}>
                                                    {['Type', 'Purpose', 'Duration'].map(h => (
                                                        <th key={h} className="text-white text-left px-4 py-3 font-semibold">{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[
                                                    ['Essential', 'Authentication, session management, CSRF protection', 'Session / 7 days'],
                                                    ['Functional', 'Remembering your preferences and form data', 'Up to 30 days'],
                                                    ['Analytics', 'Understanding page usage and improving performance (anonymised)', 'Up to 12 months'],
                                                ].map(([type, purpose, duration], i) => (
                                                    <tr
                                                        key={type}
                                                        className="border-b border-gray-100"
                                                        style={{ background: i % 2 === 0 ? '#fff' : '#f7fafc' }}
                                                    >
                                                        <td className="px-4 py-3 font-medium" style={{ color: 'var(--heading-color)' }}>{type}</td>
                                                        <td className="px-4 py-3 text-gray-600">{purpose}</td>
                                                        <td className="px-4 py-3 text-gray-500">{duration}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p>
                                        You can control or delete cookies through your browser settings at any time. Note that
                                        disabling essential cookies may prevent certain features of our platform from functioning correctly.
                                    </p>
                                </PolicySection>

                                {/* 9 – Children */}
                                <PolicySection id="children" number="9" title="Children's Privacy">
                                    <p>
                                        Our services are not directed to individuals under the age of 18. We do not knowingly collect
                                        personal information from children. If you believe a child has provided us with personal data,
                                        please contact us immediately and we will take steps to delete the information promptly.
                                    </p>
                                </PolicySection>

                                {/* 10 – Changes */}
                                <PolicySection id="changes" number="10" title="Changes to This Policy">
                                    <p>
                                        We may update this Privacy Policy from time to time to reflect changes in our practices, legal
                                        requirements, or service offerings. When we make material changes, we will:
                                    </p>
                                    <BulletList items={[
                                        'Update the "Last updated" date at the top of this page.',
                                        'Display a notice on our website for a period of at least 30 days.',
                                        'Where required by law, seek your consent before applying the changes.',
                                    ]} />
                                    <p>
                                        We encourage you to review this policy periodically. Continued use of our platform after changes
                                        take effect constitutes your acceptance of the revised policy.
                                    </p>
                                </PolicySection>

                                {/* 11 – Contact */}
                                <PolicySection id="contact" number="11" title="Contact Us">
                                    <p>
                                        If you have any questions, concerns, or requests relating to this Privacy Policy or our handling
                                        of your personal data, please reach out to us:
                                    </p>

                                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                        <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-3">
                                            <p className="font-bold text-sm uppercase tracking-wider" style={{ color: 'var(--accent-color)' }}>
                                                Postal Address
                                            </p>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                Alternative Energy (Pty) Ltd<br />
                                                Lot 810 Cnr Lifahlawane St & Shiselweni Ave<br />
                                                Matsapha M202<br />
                                                Eswatini
                                            </p>
                                        </div>
                                        <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-3">
                                            <p className="font-bold text-sm uppercase tracking-wider" style={{ color: 'var(--accent-color)' }}>
                                                Contact Details
                                            </p>
                                            <a
                                                href="mailto:info@alternative.co.sz"
                                                className="flex items-center gap-2 text-sm transition-colors duration-200"
                                                style={{ color: 'var(--accent-color)' }}
                                            >
                                                <FiMail /> info@alternative.co.sz
                                            </a>
                                            <a
                                                href="tel:+26825185414"
                                                className="flex items-center gap-2 text-sm transition-colors duration-200"
                                                style={{ color: 'var(--accent-color)' }}
                                            >
                                                <FiPhone /> +268 2518 5414
                                            </a>
                                            <a
                                                href="tel:+26825187424"
                                                className="flex items-center gap-2 text-sm transition-colors duration-200"
                                                style={{ color: 'var(--accent-color)' }}
                                            >
                                                <FiPhone /> +268 2518 7424
                                            </a>
                                        </div>
                                    </div>

                                    <InfoBox className="mt-6">
                                        We aim to respond to all privacy enquiries within <strong>5 business days</strong>.
                                    </InfoBox>
                                </PolicySection>

                            </div>

                            {/* Back to top / footer nav */}
                            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-200">
                                <p className="text-sm text-gray-400">
                                    © {new Date().getFullYear()} Alternative Energy (Pty) Ltd. All rights reserved.
                                </p>
                                <div className="flex items-center gap-4 text-sm">
                                    <Link to="/" className="transition-colors duration-200" style={{ color: 'var(--accent-color)' }}>
                                        ← Back to Home
                                    </Link>
                                    <button
                                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-200 hover:shadow-md"
                                        style={{ background: 'var(--accent-color)' }}
                                    >
                                        ↑ Back to Top
                                    </button>
                                </div>
                            </div>

                        </main>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ── Shared sub-components ── */

const PolicySection = ({ id, number, title, children }) => (
    <div
        id={id}
        className="bg-white rounded-2xl shadow-sm p-8 scroll-mt-28"
        data-aos="fade-up"
    >
        <div className="flex items-center gap-3 mb-5">
            <span
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ background: 'var(--accent-color)' }}
            >
                {number}
            </span>
            <h2
                className="text-xl font-bold m-0"
                style={{ color: 'var(--heading-color)', fontFamily: 'var(--heading-font)' }}
            >
                {title}
            </h2>
        </div>
        <div className="text-gray-600 leading-relaxed space-y-4 text-sm md:text-base">
            {children}
        </div>
    </div>
);

const SubHeading = ({ children }) => (
    <p
        className="font-semibold mt-4 mb-2"
        style={{ color: 'var(--heading-color)' }}
    >
        {children}
    </p>
);

const BulletList = ({ items }) => (
    <ul className="space-y-2 ml-1">
        {items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
                <span
                    className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: 'var(--accent-color)' }}
                />
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const InfoBox = ({ children, className = '' }) => (
    <div
        className={`rounded-xl p-4 text-sm ${className}`}
        style={{
            background: 'color-mix(in srgb, var(--accent-color), transparent 90%)',
            borderLeft: '3px solid var(--accent-color)',
            color: 'var(--heading-color)',
        }}
    >
        {children}
    </div>
);

export default PrivacyPolicy;